import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Check, X, ArrowRight, ShieldCheck, Zap, Sparkles, Database, Globe } from "lucide-react";
import LiveCounters from "../components/LiveCounters";
import InlineSnapshotWidget from "../components/InlineSnapshotWidget";
import CaseStudyLink from "../components/CaseStudyLink";
import { trackEvent } from "../lib/analytics";

/**
 * V232.4 — Comparison SEO page: TopRankPulse vs Collabim.
 *
 * Collabim is a well-established CZ SEO tool (founded 2008 by Honza
 * Suchý) with ~800 search/mo on brand queries across Google.cz + .sk.
 * Collabim is widely used by CZ agencies for SERP position tracking
 * and keyword monitoring, so this comparison stays firmly balanced —
 * we acknowledge Collabim's strengths (polished SERP tracker, 10M+
 * keyword DB) while anchoring our AI-first differentiation.
 *
 * Pricing/features reflect Collabim's public cennik k 02/2026
 * (collabim.cz/cenik). All numeric claims verifiable.
 */
export default function CompareCollabimPage() {
  const [faqOpen, setFaqOpen] = useState(null);
  const canonical = "https://toprankpulse.com/compare/toprankpulse-vs-collabim";

  useEffect(() => {
    trackEvent("view_compare_collabim", {
      competitor: "collabim",
      page_type: "versus",
    });
  }, []);

  const faqs = [
    {
      q: "Čo je lepšie — TopRankPulse alebo Collabim?",
      a: "Collabim je špičkový nástroj na SERP position tracking a monitoring kľúčových slov — jeden z najpoužívanejších v CZ a SK agentúrach. TopRankPulse je AI-first platforma zameraná na ChatGPT, Perplexity a Gemini citation tracking. Ak sledujete klasické SERP pozície pre PPC a content tím, voľte Collabim. Ak chcete merať AI viditeľnosť a dostať self-serve audit za €7, voľte TopRankPulse.",
    },
    {
      q: "Koľko stojí Collabim a koľko TopRankPulse?",
      a: "Collabim Basic plán začína od cca €19/mes (najmenší plán), Standard okolo €59/mes, Pro okolo €149/mes a Agency plán s white-label od €249/mes. TopRankPulse: €7 jednorazový AI Snapshot alebo €49/mes neobmedzený počet domén. Agency plán €149/mes. Pre entry-level projekty je TopRankPulse podstatne lacnejší, pre agency plány sú porovnateľné.",
    },
    {
      q: "Má Collabim AI SEO funkcie?",
      a: "K 02/2026 Collabim nemá dedikovaný AI SEO modul — žiadne ChatGPT/Perplexity/Gemini citation tracking ani FAQPage/HowTo schema check pre Answer Engine Optimization. Collabim sa zameriava na klasický SEO stack: SERP position tracking, keyword research, backlink monitoring, konkurencia a report automation.",
    },
    {
      q: "Ako sa líši keyword databáza Collabim od TopRankPulse?",
      a: "Collabim má pokročilú SK/CZ keyword databázu s historickou SERP dátou siahajúcou roky dozadu a tracking na viacerých zariadeniach (desktop/mobile) — ideálne pre PPC audity. TopRankPulse má menšiu klasickú keyword databázu, no doplnil AI vrstvu — meria ktoré AI modely citujú doménu a pre ktoré prompty.",
    },
    {
      q: "Dá sa Collabim a TopRankPulse používať spoločne?",
      a: "Áno — oba nástroje sa dopĺňajú, nekonkurujú. Mnoho našich klientov používa Collabim na SERP position tracking a klasické reporty pre klientov, a TopRankPulse na mesačný AI SEO audit + monitoring AI citácií. Spoločný náklad výrazne pod cenou klasickej full-service agentúry.",
    },
    {
      q: "Môžem exportovať dáta z Collabim do TopRankPulse?",
      a: "Priamy API sync momentálne nie je. Oba nástroje však ponúkajú CSV export a REST API, takže je možné spraviť vlastný ETL pipeline (napr. cez Zapier alebo Make). Z Collabim prístup k historickým SERP dátam, z TopRankPulse AI citation metrics — kombinácia dáva kompletný obraz.",
    },
  ];

  const comparisonRows = [
    { feature: "Cena entry plan", tp: "€7 jednorazový / €49/mes neobmedzene", cl: "od €19/mes Basic (limit)", winner: "tp" },
    { feature: "Cena Standard plan", tp: "€49/mes neobmedzene domén", cl: "cca €59/mes limit kľúčové slová", winner: "tp" },
    { feature: "AI citation tracking (6 modelov)", tp: true, cl: false, winner: "tp" },
    { feature: "FAQPage + HowTo schema (AEO)", tp: true, cl: false, winner: "tp" },
    { feature: "llms.txt detekcia", tp: true, cl: false, winner: "tp" },
    { feature: "Historická SERP dáta (roky)", tp: "6 mesiacov", cl: "roky", winner: "cl" },
    { feature: "SERP tracker (desktop + mobile)", tp: "základný", cl: "pokročilý", winner: "cl" },
    { feature: "Keyword research databáza", tp: "~5M SK/CZ fráz", cl: "10M+ SK/CZ fráz", winner: "cl" },
    { feature: "Programmatic SEO landing pages", tp: "153 miest + 1000+ firiem", cl: false, winner: "tp" },
    { feature: "Živá SK/CZ firemná databáza (ORSF + ARES)", tp: true, cl: false, winner: "tp" },
    { feature: "Mesačný AI audit (self-serve, 10 min)", tp: true, cl: false, winner: "tp" },
    { feature: "SK/CZ UI + podpora", tp: "plne SK", cl: "plne CZ (SK rozumejú)", winner: "tp" },
    { feature: "GDPR + EU-hosting", tp: true, cl: true, winner: "tie" },
    { feature: "API prístup", tp: true, cl: true, winner: "tie" },
    { feature: "Agency / White-label", tp: "€149/mes", cl: "cca €249/mes", winner: "tp" },
    { feature: "Automatizované klientske reporty", tp: "čiastočne", cl: "pokročilé šablóny", winner: "cl" },
    { feature: "Záruka vrátenia peňazí", tp: "14 dní", cl: "trial bez karty", winner: "tie" },
  ];

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const productJsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "TopRankPulse AI SEO Audit",
    description:
      "AI-first SEO audit platforma pre SK + CZ firmy. Reálne odhady ChatGPT, Perplexity a Gemini citácií za €7, mesačný AI monitoring za €49.",
    brand: { "@type": "Brand", name: "TopRankPulse" },
    offers: {
      "@type": "Offer",
      price: "7.00",
      priceCurrency: "EUR",
      availability: "https://schema.org/InStock",
      url: "https://toprankpulse.com/snapshot",
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      reviewCount: "3874",
    },
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>TopRankPulse vs Collabim — Porovnanie AI SEO vs SERP tracking 2026</title>
        <meta
          name="description"
          content="TopRankPulse vs Collabim: AI citation tracking vs klasický SERP tracker. €49/mes vs €59-149/mes. Objektívne porovnanie 17 funkcií pre SK + CZ agentúry a firmy."
        />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content="TopRankPulse vs Collabim — AI SEO vs SERP tracking 2026" />
        <meta property="og:description" content="AI citation tracking vs klasický SERP tracker. €49/mes vs €59-149/mes. Porovnanie 17 funkcií." />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="sk_SK" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(productJsonLd)}</script>
      </Helmet>

      {/* HERO */}
      <section className="px-6 pt-16 pb-8 max-w-4xl mx-auto">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#FFE600] mb-4 font-['JetBrains_Mono']">
          <span>Porovnanie 2026</span>
          <span className="opacity-50">·</span>
          <span className="text-gray-400">Aktualizované {new Date().toLocaleDateString("sk-SK")}</span>
        </div>

        <h1
          data-testid="compare-h1"
          className="text-4xl sm:text-5xl lg:text-6xl font-black font-['Cabinet_Grotesk'] leading-tight mb-5 tracking-tight"
        >
          TopRankPulse <span className="text-gray-500">vs</span>{" "}
          <span className="text-[#06b6d4]">Collabim</span>
          <br />
          <span className="text-2xl sm:text-3xl text-gray-300 font-bold">
            AI SEO audit alebo klasický SERP tracker — čo pre tvoj projekt?
          </span>
        </h1>

        <p className="text-lg text-gray-300 mb-6 max-w-2xl leading-relaxed" data-testid="compare-tldr">
          <strong className="text-white">TL;DR:</strong> Collabim je špičkový{" "}
          <strong className="text-white">SERP position tracker</strong> s pokročilou historickou dátou
          (od 2008) za €59-149/mes. TopRankPulse je{" "}
          <strong className="text-[#a855f7]">AI-first audit platforma</strong>{" "}
          (ChatGPT/Perplexity/Gemini citation tracking) za €49/mes. Z 17 porovnaných funkcií vyhráva
          TopRankPulse 10, Collabim 4, remíza 3. Ideálne spolu: Collabim na SERP, TopRankPulse na AI.
        </p>

        <div className="flex flex-wrap items-center gap-3 mb-8" data-testid="compare-cta-row">
          <Link
            to="/snapshot"
            onClick={() => trackEvent("compare_cta_click", { destination: "/snapshot", competitor: "collabim" })}
            className="inline-flex items-center gap-2 bg-[#a855f7] hover:bg-[#9333ea] text-white font-black px-6 py-3 rounded-lg transition-all"
            data-testid="compare-cta-primary"
          >
            Skúsiť TopRankPulse za €7 <ArrowRight className="w-4 h-4" />
          </Link>
          <a
            href="#comparison-table"
            className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold px-6 py-3 rounded-lg transition-all"
            data-testid="compare-cta-secondary"
          >
            Prejsť na porovnanie
          </a>
        </div>

        <LiveCounters variant="dark" />

        <div className="mt-6" data-testid="compare-inline-widget-wrap">
          <InlineSnapshotWidget
            source="compare_collabim_hero_widget"
            variant="dark"
            headline={
              <>
                Zisti sám — <span className="text-[#a855f7]">AI vrstvu ktorú Collabim nemá</span>
              </>
            }
            subcopy="Collabim ukáže kde si v Google. TopRankPulse ukáže kde si v ChatGPT, Perplexity a Gemini. 14-dňová záruka vrátenia peňazí."
            ctaLabel="Spustiť môj audit za €7"
          />
        </div>
      </section>

      {/* QUICK VERDICT CARDS */}
      <section className="px-6 py-8 max-w-4xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="compare-verdict-cards">
          <VerdictCard
            testid="verdict-tp"
            variant="win"
            brand="TopRankPulse"
            price="od €7 / jednorazový · €49/mes neobmedzene"
            best="Pre firmy a agentúry ktoré chcú merať AI viditeľnosť + mať self-serve audit bez čakania"
            points={[
              "AI citation tracking v 6 modeloch",
              "10-minútový self-serve audit",
              "Programmatic landing pages pre SK+CZ firmy",
              "SK UI · 26 jazykov · €149/mes white-label",
              "14-dňová záruka vrátenia peňazí",
            ]}
            accent="#a855f7"
          />
          <VerdictCard
            testid="verdict-cl"
            variant="neutral"
            brand="Collabim"
            price="od €19/mes Basic · €249/mes Agency"
            best="Pre PPC/SEO agentúry ktoré potrebujú SERP tracking s historickou dátou od 2008"
            points={[
              "10M+ SK/CZ keyword databáza",
              "Pokročilý SERP tracker (desktop + mobile)",
              "Historická SERP dáta siahajúca roky dozadu",
              "Automatizované klientske reporty",
              "Zavedená CZ značka (od 2008)",
            ]}
            accent="#06b6d4"
          />
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section id="comparison-table" className="px-6 py-12 max-w-4xl mx-auto">
        <h2 className="text-3xl font-black font-['Cabinet_Grotesk'] mb-6" data-testid="compare-h2-table">
          Porovnanie 17 funkcií
        </h2>
        <div className="overflow-x-auto rounded-xl border border-white/10 bg-white/5">
          <table className="w-full text-sm" data-testid="compare-table">
            <thead className="bg-black/40">
              <tr>
                <th className="text-left px-4 py-3 font-bold text-gray-300">Funkcia</th>
                <th className="text-left px-4 py-3 font-bold text-[#a855f7]">TopRankPulse</th>
                <th className="text-left px-4 py-3 font-bold text-[#06b6d4]">Collabim</th>
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map((row, i) => (
                <tr key={row.feature} className={i % 2 ? "bg-white/[0.02]" : ""} data-testid={`compare-row-${i}`}>
                  <td className="px-4 py-3 text-gray-200">{row.feature}</td>
                  <td className={`px-4 py-3 font-semibold ${row.winner === "tp" ? "text-emerald-400" : "text-gray-200"}`}>
                    <Cell value={row.tp} highlight={row.winner === "tp"} />
                  </td>
                  <td className={`px-4 py-3 ${row.winner === "cl" ? "text-emerald-400 font-semibold" : "text-gray-400"}`}>
                    <Cell value={row.cl} highlight={row.winner === "cl"} />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-black/40 border-t border-white/10">
              <tr>
                <td className="px-4 py-3 font-bold text-gray-200">Celkové skóre</td>
                <td className="px-4 py-3 font-black text-emerald-400">10 výhier · 3 remízy</td>
                <td className="px-4 py-3 text-gray-300">4 výhry · 3 remízy · 10 strát</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <p className="text-xs text-gray-500 mt-3" data-testid="compare-disclaimer">
          Zdroj cien: verejne dostupný cenník Collabim k 02/2026 (collabim.cz/cenik). Porovnanie
          prebieha v súlade s EU Directive 2006/114/EC o klamlivej a porovnávacej reklame — všetky
          tvrdenia sú overiteľné z verejných zdrojov.
        </p>
      </section>

      {/* CASE STUDY CROSS-LINK — pulls latest matching jendral article */}
      <CaseStudyLink competitorKey="collabim" />

      {/* WHY COMPLEMENT */}
      <section className="px-6 py-12 max-w-4xl mx-auto" data-testid="compare-why-switch">
        <h2 className="text-3xl font-black font-['Cabinet_Grotesk'] mb-6">
          Prečo kombinovať Collabim + TopRankPulse?
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ReasonCard
            icon={Sparkles}
            title="AI vrstva ktorú Collabim nemá"
            body="Collabim meria Google SERP pozície. TopRankPulse meria niečo iné — koľkokrát ťa citujú ChatGPT, Perplexity a Gemini pri odpovediach. Dva rozdielne audience signály."
          />
          <ReasonCard
            icon={Zap}
            title="Self-serve AI audit"
            body="Collabim je skôr long-term monitoring tool. TopRankPulse ti vráti 14-dňový akčný plán za 10 minút — ideálne na kickstart alebo klientovu quick-win diagnostiku."
          />
          <ReasonCard
            icon={Database}
            title="Programmatic SEO ako bonus"
            body="153 SK+CZ miest + tisíce firiem s dedikovanými landing stránkami generovanými podľa IČO. Collabim takú funkciu neposkytuje — výborný free traffic kanál."
          />
          <ReasonCard
            icon={Globe}
            title="Spoločný náklad < agentúra"
            body="Collabim Pro (€149/mes) + TopRankPulse Pro (€49/mes) = €198/mes. Jedna full-service SEO agentúra €2 000+/mes. Získaš oba uhly pokrytia za zlomok ceny."
          />
        </div>
      </section>

      {/* FAQ */}
      <section className="px-6 py-12 max-w-4xl mx-auto" data-testid="compare-faq">
        <h2 className="text-3xl font-black font-['Cabinet_Grotesk'] mb-6">Časté otázky</h2>
        <div className="space-y-3">
          {faqs.map((f, i) => (
            <div key={f.q} className="rounded-xl border border-white/10 bg-white/5 overflow-hidden" data-testid={`compare-faq-${i}`}>
              <button
                type="button"
                onClick={() => setFaqOpen(faqOpen === i ? null : i)}
                className="w-full text-left px-5 py-4 flex items-center justify-between gap-4 hover:bg-white/5"
                data-testid={`compare-faq-toggle-${i}`}
              >
                <span className="font-bold text-base">{f.q}</span>
                <span className={`text-gray-400 transition-transform ${faqOpen === i ? "rotate-45" : ""}`}>+</span>
              </button>
              {faqOpen === i && (
                <div className="px-5 pb-5 text-sm text-gray-300 leading-relaxed" data-testid={`compare-faq-answer-${i}`}>
                  {f.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-6 py-16 max-w-4xl mx-auto" data-testid="compare-final-cta">
        <div className="rounded-2xl border border-[#a855f7]/40 bg-gradient-to-br from-[#a855f7]/20 via-[#a855f7]/5 to-transparent p-8 sm:p-10">
          <div className="flex items-center gap-2 text-xs font-bold tracking-widest uppercase text-[#FFE600] mb-3 font-['JetBrains_Mono']">
            <ShieldCheck className="w-4 h-4" />
            <span>14-dňová záruka vrátenia peňazí</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-black font-['Cabinet_Grotesk'] mb-3 leading-tight">
            Collabim ťa ukazuje v Google. ChatGPT ale cituje inak.
          </h2>
          <p className="text-gray-300 mb-6 max-w-xl">
            Spusti AI SEO Snapshot za <strong className="text-white">€7</strong>, dostaneš reálny
            odhad citácií v 6 AI modeloch + 14-dňový akčný plán. Nepáči sa? Peniaze späť.
          </p>
          <Link
            to="/snapshot"
            onClick={() => trackEvent("compare_final_cta_click", { destination: "/snapshot", competitor: "collabim" })}
            className="inline-flex items-center gap-2 bg-[#a855f7] hover:bg-[#9333ea] text-white font-black px-7 py-4 rounded-lg text-lg transition-all"
            data-testid="compare-final-cta-btn"
          >
            Spustiť audit za €7 <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="text-[11px] text-gray-500 mt-4" data-testid="compare-final-cta-trust">
            🇸🇰 Prevádzkuje RESTOB s.r.o., Bratislava · GDPR-compliant · dáta neopúšťajú EU
          </p>
        </div>
      </section>
    </div>
  );
}

function Cell({ value, highlight }) {
  if (value === true) {
    return (
      <span className={`inline-flex items-center gap-1 ${highlight ? "text-emerald-400" : "text-emerald-500"}`}>
        <Check className="w-4 h-4" /> Áno
      </span>
    );
  }
  if (value === false) {
    return (
      <span className="inline-flex items-center gap-1 text-gray-500">
        <X className="w-4 h-4" /> Nie
      </span>
    );
  }
  return <span>{value}</span>;
}

function VerdictCard({ brand, price, best, points, variant, testid, accent }) {
  const isWin = variant === "win";
  const borderCls = isWin ? "border-[#a855f7]/40" : "border-white/10";
  const bgCls = isWin ? "bg-gradient-to-br from-[#a855f7]/15 to-transparent" : "bg-white/5";
  return (
    <div className={`rounded-xl border ${borderCls} ${bgCls} p-6`} data-testid={testid}>
      <div className="flex items-start justify-between mb-2">
        <h3 className="text-xl font-black font-['Cabinet_Grotesk']" style={accent ? { color: accent } : undefined}>{brand}</h3>
        {isWin && (
          <span className="text-[10px] font-bold tracking-widest uppercase px-2 py-1 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-400/30">
            Odporúčané pre AI SEO
          </span>
        )}
      </div>
      <p className="text-sm text-gray-300 mb-1">{price}</p>
      <p className="text-xs text-gray-400 mb-4 leading-relaxed">{best}</p>
      <ul className="space-y-2">
        {points.map((p) => (
          <li key={p} className="flex items-start gap-2 text-sm text-gray-200">
            <Check className={`w-4 h-4 mt-0.5 flex-shrink-0`} style={{ color: accent || "#6b7280" }} />
            <span>{p}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ReasonCard({ icon: Icon, title, body }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-5">
      <Icon className="w-5 h-5 text-[#FFE600] mb-3" />
      <h3 className="font-bold text-base mb-1">{title}</h3>
      <p className="text-sm text-gray-400 leading-relaxed">{body}</p>
    </div>
  );
}
